<template>
  <div class="base-form phone-form">
    <label
      for="auth-phone"
      class="base-label phone-form__title"
      >Телефон</label
    >

    <PhoneInput
      id="auth-phone"
      placeholder="+7 (xxx) xxx-xx-xx"
      class="phone-form__input base-input base-input--black"
      @change="setPhone"
    />

    <div class="input-error">
      <template v-if="errorMessage">
        {{ errorMessage }}
      </template>
    </div>

    <template v-if="isVisibleCaptcha">
      <div id="captcha-container" />

      <div class="input-error">
        <template v-if="captchaError">
          {{ captchaError }}
        </template>
      </div>
    </template>

    <div class="phone-form__confirmation">
      <label
        for="processing-personal-data"
        class="checkbox-container"
      >
        <input
          id="processing-personal-data"
          v-model="isConfirmed"
          type="checkbox"
        />
        <span class="checkmark" />
        <span class="phone-form__confirmation-message">
          Настоящим я даю согласие на обработку моих персональных данных в порядке и в целях,
          определенных
          <a
            href="/docs/license-agreement/"
            target="_blank"
            rel="noreferrer noopener"
          >
            Политикой конфиденциальности</a
          >.
        </span>
      </label>
    </div>

    <button
      class="phone-form__get-code-btn base-btn base-btn--black"
      :class="{
        'base-btn--disabled': !isSendCodeAllowed,
      }"
      @click="send"
    >
      Получить код
    </button>
  </div>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate';
import { string } from 'yup';
import MoCaptcha from '@mo/captcha';

import { PhoneInput } from '../phone-input';

const props = defineProps({
  errorMessage: {
    type: String,
    default: '',
  },
  isVisibleCaptcha: {
    type: Boolean,
    default: false,
  },
  captchaError: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['send']);

const { value: phone, meta } = useField<string>(
  'phone',
  string()
    .required()
    .matches(/\+\d{11}/, 'Номер телефона должен содержать 11 цифр.'),
);

const captchaToken = ref<string | null>(null);
const moCaptcha = ref<MoCaptcha | null>(null);
const isConfirmed = ref<boolean>(false);

const isSendCodeAllowed = computed(() => {
  return (
    meta.valid && isConfirmed.value && (!props.isVisibleCaptcha || captchaToken.value !== null)
  );
});

onMounted(() => {
  if (!props.isVisibleCaptcha) {
    return;
  }

  initMoCaptcha();
});

const initMoCaptcha = () => {
  moCaptcha.value = new MoCaptcha('captcha-container', {
    titleText: 'Вы не робот?',
    manualText: 'Проведите вправо',
    onSuccess,
    onFail,
  });
};

const onSuccess = (token: string) => {
  captchaToken.value = token;
};

const onFail = () => {};

const setPhone = (phoneValue: string) => {
  phone.value = phoneValue;
};

const send = () => {
  if (!meta.valid || (props.isVisibleCaptcha && !captchaToken.value)) {
    return;
  }

  emit('send', phone.value, captchaToken.value);
};

watch(
  () => props.isVisibleCaptcha,
  async (newVal) => {
    if (!newVal) {
      return;
    }
    await nextTick();
    initMoCaptcha();
  },
  { immediate: true },
);

watch(
  () => props.captchaError,
  (newVal) => {
    if (!newVal) {
      return;
    }

    moCaptcha.value?.reset();
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.phone-form {
  &__title {
    font-family: 'Gilroy-Medium', sans-serif;
    line-height: 24px;
    font-size: 16px;
  }

  &__input {
    font-family: 'Gilroy-Medium', sans-serif;
    font-size: 18px;
    line-height: 24px;
  }

  &--error {
    input {
      border-color: #ff4646 !important;
    }

    &:after {
      display: none;
    }
  }

  #captcha-container {
    display: flex;
    justify-content: center;
  }

  &__confirmation {
    margin: 12px 0 22px;
  }

  &__confirmation-message {
    font-family: 'Gilroy-Medium', sans-serif;
    font-size: 15px;
    line-height: 19px;
    display: inline-block;
    vertical-align: middle;
    color: rgb(135, 135, 145);

    a {
      color: #000;
      text-decoration: none;
    }
  }

  .checkbox-container {
    display: block;
    position: relative;
    padding-left: 40px;
    cursor: pointer;

    input {
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      cursor: pointer;
      margin-top: 0;

      &:checked ~ .checkmark {
        border-color: #000;
        background-color: #000;

        &:after {
          display: block;
        }
      }
    }

    .checkmark {
      position: absolute;
      width: 22px;
      height: 22px;
      top: 0;
      left: 0;
      background-color: #fff;
      border: 1px solid #d8d8d8;
    }

    & input:checked ~ .checkmark {
      background-color: #000;
    }

    & input:checked ~ .phone-form__confirmation-message {
      color: #000;
    }

    .checkmark:after {
      content: '';
      position: absolute;
      display: none;
    }

    & input:checked ~ .checkmark:after {
      display: block;
    }

    & .checkmark:after {
      left: 7px;
      top: 3px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }

  &__get-code-btn {
    width: 100%;
    font-family: 'Gilroy-Medium', sans-serif;
    font-size: 21px;
    line-height: 21px;
    padding-top: 8px;
    align-items: center;
  }
}

@media (max-width: 375px) {
  .phone-form {
    #captcha-container {
      :deep(.mo-captcha-widget) {
        border: none !important;
        padding: 0px !important;
      }
    }
  }
}
</style>
